import { useState } from "react";
import { Palette, Pencil, Trash2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { deletePalette, renamePalette, usePalettes } from "@/lib/palettes";
import { activeTheme, applyPalette, useTheme } from "@/lib/theme";
import { formatApiError } from "@/lib/api";

export const PaletteRenameDialog = ({ palette, onClose }) => {
  const [name, setName] = useState(palette?.name || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await renamePalette(palette.id, name.trim()); toast.success("Palette renamed"); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!palette} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-sm" data-testid="palette-rename-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Rename palette</DialogTitle></DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} autoFocus onKeyDown={(e) => e.key === "Enter" && name.trim() && save()} data-testid="palette-rename-input" />
        <DialogFooter>
          <button onClick={save} disabled={busy || !name.trim()} data-testid="palette-rename-save" className="flex items-center gap-2 rounded-full bg-zinc-900 px-6 py-2 text-sm text-white disabled:opacity-40">{busy && <Loader2 size={14} className="animate-spin" />}Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Settings → My settings: the account's saved background palettes with apply / rename / delete. */
export const PalettesSection = () => {
  const saved = usePalettes();
  const s = useTheme();
  const theme = activeTheme(s);
  const [renaming, setRenaming] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const remove = async (p) => {
    if (!window.confirm(`Delete “${p.name}”?`)) return;
    setBusyId(p.id);
    try { await deletePalette(p.id); toast.success(`Deleted “${p.name}”`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };
  const btn = "flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200 text-zinc-500 transition-colors hover:border-zinc-900 hover:text-zinc-900 disabled:opacity-40";
  return (
    <section className="mt-8 rounded-lg border border-zinc-200 bg-white p-8" data-testid="palettes-section">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-fuchsia-50 text-fuchsia-700"><Palette size={18} /></div>
        <div>
          <h2 className="text-lg font-medium">Saved palettes</h2>
          <p className="text-xs text-zinc-500">Background colours you saved from cover art. They follow your account on every device — up to 12.</p>
        </div>
      </div>
      {saved.length === 0 ? (
        <p className="mt-6 text-sm text-zinc-500" data-testid="palettes-empty">Nothing saved yet. Use the palette button in the top bar → “Shuffle from cover art” → “Save this palette”.</p>
      ) : (
        <ul className="mt-6 divide-y divide-zinc-100" data-testid="palettes-list">
          {saved.map((p) => {
            const active = theme?.kind === "art" && theme.id === `saved:${p.id}`;
            return (
              <li key={p.id} className="flex items-center gap-4 py-3" data-testid={`palette-row-${p.id}`}>
                <span className="flex h-8 w-24 overflow-hidden rounded-md ring-1 ring-black/10">{p.colors.map((c, i) => <span key={i} className="flex-1" style={{ background: c }} />)}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-zinc-800" data-testid={`palette-name-${p.id}`}>{p.name}</p>
                  <p className="text-[11px] text-zinc-400">{p.colors.length} colours · saved {new Date(p.created_at).toLocaleDateString()}</p>
                </div>
                <button type="button" onClick={() => applyPalette(p)} disabled={active} className={`flex h-8 items-center gap-1.5 rounded-full px-3 text-xs transition-colors ${active ? "bg-amber-50 text-amber-700" : "border border-zinc-200 text-zinc-700 hover:border-zinc-900"}`} data-testid={`palette-apply-${p.id}`}>{active ? <><Check size={12} />Active</> : "Apply"}</button>
                <button type="button" onClick={() => setRenaming(p)} className={btn} data-testid={`palette-rename-${p.id}`} title="Rename" aria-label={`Rename ${p.name}`}><Pencil size={13} /></button>
                <button type="button" onClick={() => remove(p)} disabled={busyId === p.id} className={`${btn} hover:border-red-600 hover:text-red-600`} data-testid={`palette-delete-${p.id}`} title="Delete" aria-label={`Delete ${p.name}`}>{busyId === p.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}</button>
              </li>
            );
          })}
        </ul>
      )}
      {renaming && <PaletteRenameDialog palette={renaming} onClose={() => setRenaming(null)} />}
    </section>
  );
};
